import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import Layout from '../components/layout/Layout';
import Button from '../components/common/Button';
import HowItWorksDiagram from '../components/solutions/HowItWorksDiagram';
import ImplementationSteps from '../components/solutions/ImplementationSteps';
import SolutionMedia from '../components/solutions/SolutionMedia';
import { CheckIcon } from '@heroicons/react/outline';
import { BadgeCheckIcon, ArrowLeftIcon } from '@heroicons/react/solid';
import { Solution, SolutionTier, Feature, Challenge, Sector } from '../types/solution.types';
import { getSolutionById } from '../services/solution.service';

interface TierBadgeInfo {
  text: string;
  style: string;
}

const tierBadges: Record<SolutionTier, TierBadgeInfo> = {
  bronze: { text: 'Bronze Tier', style: 'bg-amber-600 text-white' },
  silver: { text: 'Silver Tier', style: 'bg-slate-500 text-white' },
  gold: { text: 'Gold Tier', style: 'bg-accent text-accent-foreground dark:bg-dark-accent dark:text-dark-accent-foreground' },
};

const SolutionDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [solution, setSolution] = useState<Solution | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  
  useEffect(() => {
    const fetchSolution = async () => {
      if (!id) {
        navigate('/solutions');
        return;
      }
      
      setIsLoading(true);
      try {
        const fetchedSolution = await getSolutionById(id);
        if (fetchedSolution) {
          setSolution(fetchedSolution);
        } else {
          console.warn(`Solution with ID '${id}' not found.`);
          navigate('/solutions');
        }
      } catch (error) {
        console.error("Error fetching solution:", error);
        navigate('/solutions');
      } finally {
        setIsLoading(false);
      }
    };

    fetchSolution();
  }, [id, navigate]);
  
  if (isLoading) {
    return (
      <Layout>
        <div className="flex flex-col justify-center items-center min-h-[60vh] text-center">
          <div className="spinner spinner-lg"></div>
          <p className="mt-4 text-xl text-muted-foreground dark:text-dark-muted-foreground">Loading Solution...</p>
        </div>
      </Layout>
    );
  }
  
  if (!solution) {
    return (
      <Layout>
        <div className="py-12 text-center">
          <p className="text-xl text-destructive">Solution could not be loaded.</p>
        </div>
      </Layout>
    );
  }
  
  const tierBadge = tierBadges[solution.tier];
  
  return (
    <Layout>
      {/* Hero section */}
      <section className="py-16 md:py-24 bg-background dark:bg-dark-background border-b border-border/30 dark:border-dark-border/30">
        <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
          <div className="flex items-center mb-12">
            <button
              onClick={() => navigate('/solutions')}
              className="inline-flex items-center transition-colors text-foreground/60 hover:text-accent dark:text-dark-foreground/60 dark:hover:text-dark-accent font-medium text-sm tracking-wide uppercase"
            >
              <ArrowLeftIcon className="w-4 h-4 mr-2" />
              Back to Solutions
            </button>
          </div>
          
          <div className="lg:flex lg:items-center lg:justify-between">
            <div className="lg:max-w-2xl">
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${tierBadge.style} mb-6`}>
                {tierBadge.text}
              </span>
              <h1 className="text-4xl font-bold text-foreground dark:text-dark-foreground sm:text-5xl md:text-6xl tracking-tight leading-[1.1]">
                {solution.title}
              </h1>
              <p className="mt-6 text-xl text-foreground/70 dark:text-dark-foreground/70 font-light leading-relaxed max-w-xl">
                {solution.tagline}
              </p>
            </div>
            <div className="mt-10 lg:mt-0 flex flex-col gap-3 min-w-[200px]">
              {solution.productUrl ? (
                <>
                  <a
                    href={solution.productUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center px-8 py-3 rounded-full text-xs font-semibold bg-accent text-white hover:bg-accent/90 transition-colors shadow-soft uppercase tracking-wide text-center"
                  >
                    <span>Launch App</span>
                    <svg className="ml-1.5 h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </a>
                  <Button href="/contact" variant="outline" size="lg" className="w-full rounded-full px-8 uppercase tracking-wide text-xs font-semibold border-border text-foreground hover:bg-secondary dark:border-dark-border dark:text-dark-foreground dark:hover:bg-dark-secondary">
                    Get In Touch
                  </Button>
                </>
              ) : (
                <Button href="/contact" variant="accent" size="lg" className="w-full sm:w-auto rounded-full px-8 uppercase tracking-wide text-xs font-semibold">
                  Get Started
                </Button>
              )}
              <p className="text-xs text-center lg:text-right text-foreground/50 dark:text-dark-foreground/50 uppercase tracking-widest font-mono mt-2">
                {solution.productUrl ? 'SaaS Platform' : 'System Integration'}
              </p>
            </div>
          </div>
        </div>
      </section>
      
      {/* Main content - Use background, secondary background */}
      <section className="py-12 bg-background dark:bg-dark-background">
        <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
          <div className="lg:grid lg:grid-cols-3 lg:gap-8">
            {/* Left column - Description and targets */}
            <div className="lg:col-span-2">
              <div className="prose prose-lg max-w-none dark:prose-invert">
                <p className="dark:text-dark-foreground">{solution.description}</p>
              </div>
              
              {/* Add Solution Media here */}
              {solution.media && (
                <div className="mt-8">
                  <SolutionMedia media={solution.media} className="w-full" />
                </div>
              )}
              
              <div className="grid grid-cols-1 gap-8 mt-8 sm:grid-cols-2">
                <div>
                  <h3 className="text-lg font-medium text-foreground dark:text-dark-foreground">Challenges Addressed</h3>
                  <div className="mt-4 space-y-2">
                    {solution.challenges.map((challenge: Challenge, index: number) => (
                      <div key={index} className="flex items-start">
                        <CheckIcon className="flex-shrink-0 w-5 h-5 text-success dark:text-dark-success" />
                        <span className="ml-2 text-muted-foreground dark:text-dark-muted-foreground">{challenge}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-medium text-foreground dark:text-dark-foreground">Target Sectors</h3>
                  <div className="flex flex-wrap gap-2 mt-4">
                    {solution.sectors.map((sector: Sector, index: number) => (
                      <span key={index} className="inline-flex items-center px-3 py-1 text-sm font-medium rounded-full bg-accent/10 text-accent/80 dark:bg-dark-accent/10 dark:text-dark-accent">
                        {sector}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
              <div className="mt-12">
                <h2 className="text-2xl font-bold text-primary dark:text-dark-accent">Key Features</h2>
                <div className="grid grid-cols-1 gap-8 py-12 mt-6 border-t border-b border-border dark:border-dark-border sm:grid-cols-2">
                  {solution.features.map((feature: Feature, index: number) => (
                    <motion.div 
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="relative"
                    >
                      <dt>
                        <div className="absolute flex items-center justify-center w-12 h-12 rounded-md bg-secondary dark:bg-dark-secondary text-accent border border-border/50 dark:border-dark-border/50">
                          <BadgeCheckIcon className="w-6 h-6" />
                        </div>
                        <p className="ml-16 text-lg font-medium text-foreground dark:text-dark-foreground">{feature.title}</p>
                      </dt>
                      <dd className="mt-2 ml-16 text-base text-muted-foreground dark:text-dark-muted-foreground">{feature.description}</dd>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
            {/* Right column - Implementation details and animation */}
            <div className="mt-12 lg:mt-0">
              <div className="space-y-8">
                <HowItWorksDiagram 
                  nodes={solution.animation.nodes} 
                  edges={solution.animation.edges} 
                />
                <ImplementationSteps 
                  steps={solution.implementationSteps}
                  timeline={solution.timeline}
                  pricing={solution.pricing}
                />
                {solution.techStack && (
                  <div className="overflow-hidden rounded-lg bg-card dark:bg-dark-card shadow-soft dark:shadow-dark-card">
                    <h3 className="p-6 text-lg font-semibold border-b text-card-foreground dark:text-dark-accent border-border dark:border-dark-border">
                      Technology Snapshot
                    </h3>
                    <div className="p-6">
                      <ul className="space-y-2">
                        {solution.techStack.map((tech: string, index: number) => (
                          <li key={index} className="flex items-center">
                            <div className="w-2 h-2 mr-2 rounded-full bg-accent dark:bg-dark-accent"></div>
                            <span className="text-muted-foreground dark:text-dark-muted-foreground">{tech}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* CTA section */}
      <section className="py-20 bg-background dark:bg-dark-background">
        <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
          <div className="overflow-hidden rounded-2xl shadow-xl bg-card dark:bg-dark-card border border-border/60 dark:border-dark-border/60 relative">
            <div className="absolute inset-0 opacity-[0.03] dark:opacity-[0.05]" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, currentColor 1px, transparent 0)', backgroundSize: '32px 32px' }}></div>
            <div className="relative z-10 px-6 py-12 sm:p-16">
              <div className="max-w-xl mx-auto lg:max-w-none lg:mx-0 lg:flex lg:items-center lg:justify-between">
                <div>
                  <h2 className="text-3xl font-bold text-foreground dark:text-dark-foreground sm:text-4xl tracking-tight">
                    Ready to use this <span className="font-serif italic text-accent font-light">{solution.productUrl ? 'platform' : 'solution'}</span>?
                  </h2>
                  <p className="mt-4 text-lg text-foreground/70 dark:text-dark-foreground/70 font-light">
                    {solution.productUrl 
                      ? `Launch ${solution.title} now or consult with our engineering team for custom workspace configurations.` 
                      : `Consult with our engineering team today to map out the ${solution.title} integration.`}
                  </p>
                </div>
                <div className="mt-8 lg:mt-0 lg:ml-8">
                  <div className="sm:flex">
                    {solution.productUrl ? (
                      <a
                        href={solution.productUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center px-8 py-3 rounded-full text-xs font-semibold bg-accent text-white hover:bg-accent/90 transition-colors shadow-soft uppercase tracking-wide text-center w-full sm:w-auto"
                      >
                        <span>Launch App</span>
                        <svg className="ml-1.5 h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                      </a>
                    ) : (
                      <Button href="/contact" variant="accent" size="lg" className="w-full sm:w-auto rounded-full px-8 uppercase tracking-wide text-xs font-semibold">
                        Contact Us
                      </Button>
                    )}
                    <Button
                      href={solution.productUrl ? "/contact" : "/solutions"}
                      variant="outline"
                      size="lg"
                      className="w-full mt-4 sm:mt-0 sm:ml-4 sm:w-auto rounded-full px-8 uppercase tracking-wide text-xs font-semibold border-border text-foreground hover:bg-secondary dark:border-dark-border dark:text-dark-foreground dark:hover:bg-dark-secondary"
                    >
                      {solution.productUrl ? "Consult with Us" : "Explore Catalogue"}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default SolutionDetailPage;
